import { Button } from "@/components/ui/button";
import { Eye, Pencil, Trash2 } from "lucide-react";
import { ActionConfig, CustomAction } from "./types";

interface TableActionsProps<T> {
  row: T;
  actions: ActionConfig<T>;
}

/**
 * Componente de ações para cada linha da tabela.
 * Renderiza botões de visualizar, editar, deletar e ações customizadas.
 */
export function TableActions<T>({ row, actions }: TableActionsProps<T>) {
  const { onView, onEdit, onDelete, customActions } = actions;

  const hasAnyAction = onView || onEdit || onDelete || (customActions && customActions.length > 0);

  if (!hasAnyAction) return null;

  return (
    <div className="flex items-center gap-2" role="group" aria-label="Ações da linha">
      {onView && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 p-0 text-gray-400 hover:text-green-400 hover:bg-green-500/10"
          onClick={(e) => {
            e.stopPropagation();
            onView(row);
          }}
          aria-label="Visualizar"
        >
          <Eye className="w-5 h-5 text-green-400" />
        </Button>
      )}

      {onEdit && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 p-0 text-gray-400 hover:text-blue-400 hover:bg-blue-500/10"
          onClick={(e) => {
            e.stopPropagation();
            onEdit(row);
          }}
          aria-label="Editar"
        >
          <Pencil className="w-5 h-5 text-blue-400" />
        </Button>
      )}

      {onDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 p-0 text-gray-400 hover:text-red-400 hover:bg-red-500/10"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(row);
          }}
          aria-label="Deletar"
        >
          <Trash2 className="w-5 h-5 text-red-400" />
        </Button>
      )}

      {customActions?.map((action: CustomAction<T>, index: number) => (
        <Button
          key={index}
          variant="ghost"
          size="icon"
          className={`h-6 w-6 p-0 text-gray-400 hover:bg-gray-500/10 ${action.colorClass || ""}`}
          onClick={(e) => {
            e.stopPropagation();
            action.onClick(row);
          }}
          aria-label={action.label}
          title={action.label}
        >
          {action.icon}
        </Button>
      ))}
    </div>
  );
}
